// Wiring — carriers on the left grouped by domain, then repositories; each
// resource kind in its own column on the right, busiest at the top. Hover a
// node to trace it, click to pin; a dashed edge is a copy that differs.

import * as React from "react";

import { Input } from "@/components/ui/primitives";
import type { MapSource } from "@/types/api";

import {
  type Graph,
  type Kind,
  type MapNode,
  KIND_LABEL,
  SOURCE_LABEL,
  hasDrift,
  isCarrier,
  neighboursOf,
  nodesOf,
} from "./model";

export const sourceColor = (s: MapSource) => `var(--map-${s})`;

const COL = { left: 175, skill: 370, tool: 570, know: 765 } as const;
const ROW = 22;
const TOP = 34;
const WIDTH = 945;

interface Placed {
  x: number;
  y: number;
}

function layout(g: Graph) {
  const pos = new Map<string, Placed>();
  const labels: { x: number; y: number; text: string; anchor: "start" | "end" }[] = [];
  let y = TOP;
  g.data.domains.forEach((d) => {
    const agents = nodesOf(g, "agent").filter((a) => a.domainId === d.id);
    if (!agents.length) return;
    labels.push({ x: COL.left - 8, y, text: d.name, anchor: "end" });
    y += 16;
    agents.forEach((a) => {
      pos.set(a.id, { x: COL.left, y });
      y += ROW;
    });
    y += 10;
  });
  const repos = nodesOf(g, "repo");
  if (repos.length) {
    y += 6;
    labels.push({ x: COL.left - 8, y, text: "GitHub repositories", anchor: "end" });
    y += 16;
    repos.forEach((r) => {
      pos.set(r.id, { x: COL.left, y });
      y += ROW;
    });
  }
  const column = (kind: Kind, x: number, start: number) => {
    let yy = start;
    nodesOf(g, kind)
      .sort((a, b) => b.deg - a.deg || a.name.localeCompare(b.name))
      .forEach((n) => {
        pos.set(n.id, { x, y: yy });
        yy += ROW;
      });
    return yy;
  };
  const hSkill = column("skill", COL.skill, TOP);
  const hTool = column("tool", COL.tool, TOP);
  let yk = TOP;
  labels.push({ x: COL.know + 12, y: yk, text: "Knowledge bases", anchor: "start" });
  yk = column("kb", COL.know, yk + 16) + 14;
  labels.push({ x: COL.know + 12, y: yk, text: "LLM providers", anchor: "start" });
  yk = column("model", COL.know, yk + 16);
  return { pos, labels, height: Math.max(y, hSkill, hTool, yk) + 16 };
}

function Shape({ node, r }: { node: MapNode; r: number }) {
  const fill = sourceColor(node.source);
  if (node.kind === "agent") return <circle r={r} fill={fill} />;
  if (node.kind === "repo")
    return <rect x={-r} y={-r} width={2 * r} height={2 * r} rx={1.5} fill={fill} />;
  if (node.kind === "skill" || node.kind === "tool")
    return <path d={`M0,${-r} L${r},0 L0,${r} L${-r},0 Z`} fill={fill} />;
  const points = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i;
    return `${(r * 1.1 * Math.cos(a)).toFixed(2)},${(r * 1.1 * Math.sin(a)).toFixed(2)}`;
  }).join(" ");
  return <polygon points={points} fill={fill} />;
}

export function Wiring({ graph, filter }: { graph: Graph; filter: Kind | null }) {
  const { pos, labels, height } = React.useMemo(() => layout(graph), [graph]);
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [pinned, setPinned] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");

  const searched = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return [...graph.nodes.values()].find((n) => n.name.toLowerCase().includes(q))?.id ?? null;
  }, [graph, query]);
  const focus = pinned ?? hovered ?? searched;
  const lit = React.useMemo(() => {
    if (!focus) return null;
    return new Set([focus, ...neighboursOf(graph, focus).map((x) => x.id)]);
  }, [graph, focus]);

  const passesFilter = (n: MapNode) =>
    !filter || n.kind === filter || (isCarrier(n.kind) && neighboursOf(graph, n.id, filter).length);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0 space-y-3">
        <Input
          type="search"
          placeholder="Find a node…"
          aria-label="Find a node"
          className="max-w-xs"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPinned(null);
          }}
        />
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${WIDTH} ${height}`}
            className="block h-auto w-full min-w-[720px]"
            role="img"
            aria-label="Wiring diagram of agents, repositories and the resources they share"
          >
            {(
              [
                ["Agents & repos", COL.left - 10, "end"],
                ["Skills", COL.skill + 12, "start"],
                ["Tools", COL.tool + 12, "start"],
                ["Knowledge & models", COL.know + 12, "start"],
              ] as const
            ).map(([text, x, anchor]) => (
              <text
                key={text}
                x={x}
                y={16}
                textAnchor={anchor}
                className="fill-muted-foreground text-[11px] font-semibold uppercase tracking-wider"
              >
                {text}
              </text>
            ))}
            {labels.map((l) => (
              <text
                key={`${l.text}-${l.y}`}
                x={l.x}
                y={l.y + 4}
                textAnchor={l.anchor}
                className="fill-muted-foreground text-[10px] font-semibold uppercase tracking-wide"
              >
                {l.text}
              </text>
            ))}
            <g>
              {graph.edges.map((e, i) => {
                let a = pos.get(e.a);
                let b = pos.get(e.b);
                if (!a || !b) return null;
                if (a.x > b.x) [a, b] = [b, a];
                const x1 = a.x + 6;
                const x2 = b.x - 8;
                const mx = (x1 + x2) / 2;
                const on = focus !== null && (e.a === focus || e.b === focus);
                const kindA = graph.nodes.get(e.a)!.kind;
                const kindB = graph.nodes.get(e.b)!.kind;
                const filtered = filter && kindA !== filter && kindB !== filter;
                return (
                  <path
                    key={i}
                    d={`M${x1},${a.y} C${mx},${a.y} ${mx},${b.y} ${x2},${b.y}`}
                    fill="none"
                    stroke={e.variant ? "var(--map-drift)" : "hsl(var(--muted-foreground))"}
                    strokeWidth={on ? 1.8 : 1.1}
                    strokeDasharray={e.variant ? "4 3" : undefined}
                    opacity={on ? 0.9 : lit ? 0.05 : filtered ? 0.03 : 0.35}
                  />
                );
              })}
            </g>
            <g>
              {[...graph.nodes.values()].map((n) => {
                const p = pos.get(n.id);
                if (!p) return null;
                const left = isCarrier(n.kind);
                const r = 4.5 + Math.min(n.deg, 8) * 0.45;
                const dimmed = lit ? !lit.has(n.id) : !passesFilter(n);
                return (
                  <g
                    key={n.id}
                    transform={`translate(${p.x},${p.y})`}
                    className="cursor-pointer outline-none focus-visible:[&>rect]:stroke-primary"
                    opacity={dimmed ? (lit ? 0.18 : 0.12) : 1}
                    tabIndex={0}
                    role="button"
                    aria-label={`${KIND_LABEL[n.kind]} ${n.name}`}
                    aria-pressed={pinned === n.id}
                    onMouseEnter={() => setHovered(n.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setPinned((cur) => (cur === n.id ? null : n.id))}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        setPinned((cur) => (cur === n.id ? null : n.id));
                      }
                    }}
                  >
                    <rect
                      x={left ? -170 : -6}
                      y={-11}
                      width={left ? 180 : 195}
                      height={22}
                      fill="transparent"
                      strokeWidth={1}
                    />
                    <Shape node={n} r={r} />
                    {hasDrift(graph, n.id) ? (
                      <circle
                        cx={left ? -r - 4 : r + 4}
                        cy={-r - 2}
                        r={3}
                        fill="var(--map-drift)"
                      />
                    ) : null}
                    <text
                      x={left ? -10 : 12}
                      y={4}
                      textAnchor={left ? "end" : "start"}
                      className="pointer-events-none fill-foreground text-[11.5px]"
                    >
                      {n.name}
                      {left ? null : (
                        <tspan dx={6} className="fill-muted-foreground text-[10px]">
                          ×{n.deg}
                        </tspan>
                      )}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>
      <Detail graph={graph} id={focus} pinned={pinned !== null} />
    </div>
  );
}

function Chip({
  tone,
  children,
}: {
  tone: "drift" | "same" | "orphan";
  children: React.ReactNode;
}) {
  const style = {
    drift: { background: "var(--map-drift-soft)", color: "var(--map-drift)" },
    same: { background: "var(--map-aviary-soft)", color: "var(--map-aviary)" },
    orphan: { background: "var(--map-cursor-soft)", color: "var(--map-cursor)" },
  }[tone];
  return (
    <span className="inline-block rounded-full px-2 py-px text-[11px] font-semibold" style={style}>
      {children}
    </span>
  );
}

const ORDER: Kind[] = ["agent", "repo", "skill", "tool", "kb", "model"];

function Detail({ graph, id, pinned }: { graph: Graph; id: string | null; pinned: boolean }) {
  const node = id ? graph.nodes.get(id) : undefined;
  const box =
    "self-start rounded-md border bg-surface p-4 text-sm lg:sticky lg:top-4 min-w-0 space-y-3";
  if (!node) {
    return (
      <aside className={box}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Selection
        </div>
        <p className="text-muted-foreground">Nothing selected. Hover to preview, click to pin.</p>
      </aside>
    );
  }
  const near = neighboursOf(graph, node.id).map((x) => ({ ...graph.nodes.get(x.id)!, ...x }));
  const skill =
    node.kind === "skill" ? graph.data.skills.find((s) => `skill:${s.key}` === node.id) : undefined;
  const copyPath = (carrier: string) =>
    skill?.copies
      .filter((c) => c.carrier === carrier)
      .map((c) => c.path)
      .join(", ");

  return (
    <aside className={box} aria-live="polite">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {KIND_LABEL[node.kind]}
          {node.source !== "aviary" ? ` · ${SOURCE_LABEL[node.source]}` : ""}
          {pinned ? " · pinned" : ""}
        </div>
        <h3 className="mt-1 text-base font-semibold">{node.name}</h3>
        {node.sub ? (
          <div className="break-all font-mono text-xs text-muted-foreground">{node.sub}</div>
        ) : null}
        {skill ? (
          <div className="mt-2">
            {skill.variants > 1 ? (
              <Chip tone="drift">{skill.variants} content variants</Chip>
            ) : skill.carriers <= 1 ? (
              <Chip tone="orphan">orphan · one carrier</Chip>
            ) : (
              <Chip tone="same">identical in all {skill.carriers} carriers</Chip>
            )}
          </div>
        ) : null}
      </div>
      {ORDER.map((kind) => {
        const items = near.filter((x) => x.kind === kind);
        if (!items.length) return null;
        return (
          <div key={kind}>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {KIND_LABEL[kind]}s · {items.length}
            </h4>
            <ul className="divide-y">
              {items.map((x) => (
                <li key={x.id} className="flex items-start justify-between gap-2 py-1">
                  <span className="min-w-0">
                    <span className="block truncate">{x.name}</span>
                    {skill && x.kind === "repo" ? (
                      <span className="block truncate font-mono text-[11px] text-muted-foreground">
                        {copyPath(x.id)}
                      </span>
                    ) : null}
                  </span>
                  {x.variant ? <Chip tone="drift">differs</Chip> : null}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </aside>
  );
}

export { Chip };
